import { io, type Socket } from 'socket.io-client';
import type { ClientToServer, LobbyView, ServerToClient } from '@mafia/core';
import { ApiError, getToken, refreshSession } from './api';
import { setSiteClosed, useSite } from '@/store/site';
import { banNotice, useAuth } from '@/store/auth';
import { useGame } from '@/store/game';
import { errorText, noticeText } from './format';

type GameSocket = Socket<ServerToClient, ClientToServer>;
export type Ack<T = unknown> = { ok: true; data?: T } | { ok: false; error: string; message?: string };

let socket: GameSocket | null = null;
let authFailures = 0;

export function connectSocket() {
  if (socket) return socket;
  const s: GameSocket = io('/game', {
    path: '/socket.io',
    transports: ['websocket'],
    // Функция вызывается на каждой попытке подключения — подхватывает свежий токен после refresh.
    auth: (cb) => cb({ token: getToken() }),
  });
  socket = s;
  const store = useGame;
  store.setState({ connection: 'connecting' });

  s.on('connect', () => {
    authFailures = 0;
    store.setState({ connection: 'online' });
    // После обрыва (или рестарта сервера) сверяем членство в комнате: лобби могло исчезнуть.
    const lobby = store.getState().lobby;
    if (lobby) {
      void emit<LobbyView>('room:join', { roomId: lobby.roomId }, { silent: true }).then((res) => {
        if (res.ok) store.setState({ lobby: res.data ?? lobby });
        else store.setState({ lobby: null });
      });
    }
  });

  s.on('disconnect', async (reason) => {
    store.setState({ connection: 'offline' });
    // Сервер разорвал соединение сам (бан, смена пароля) — проверяем сессию.
    // Сайт закрыли (заглушка): сессия цела, переподключаться не нужно.
    if (reason === 'io server disconnect' && !useSite.getState().closed) await reauthenticate(s);
  });

  s.on('connect_error', async (err) => {
    store.setState({ connection: 'offline' });
    if (err.message === 'SITE_CLOSED') {
      setSiteClosed(true);
      s.disconnect();
      return;
    }
    if (err.message === 'UNAUTHORIZED' || err.message === 'BANNED') await reauthenticate(s);
  });

  s.on('room:state', (lobby) => {
    const { view, viewRoomId } = store.getState();
    // Снапшот игры мог прийти раньше состояния комнаты (переподключение) — привязываем его к комнате.
    const bind = view && !viewRoomId && view.gameId === lobby.gameId ? { viewRoomId: lobby.roomId } : {};
    store.setState({ lobby, ...bind });
  });

  s.on('game:state', ({ view, serverNow }) => {
    const current = store.getState().view;
    if (current && current.gameId === view.gameId && current.version > view.version) return;
    const roomId = store.getState().lobby?.roomId ?? store.getState().viewRoomId;
    store.setState({ view, viewRoomId: roomId, offset: serverNow - Date.now() });
  });

  s.on('game:notice', (notice) => {
    const text = noticeText(notice, store.getState().view);
    if (text) store.getState().toast(text.text, text.tone);
  });

  s.on('game:over', ({ view }) => store.setState({ view }));

  s.on('game:aborted', ({ reason }) => {
    store.setState({ view: null });
    store.getState().toast(`Игра прервана администратором: ${reason}`, 'bad');
  });

  s.on('room:kicked', ({ reason }) => {
    store.setState({ lobby: null });
    store.getState().toast(reason, 'bad');
  });

  s.on('room:closed', () => {
    store.setState({ lobby: null, view: null });
    store.getState().toast('Комната закрыта', 'info');
  });

  s.on('site:status', ({ open }) => setSiteClosed(!open));

  s.on('error', ({ code, message }) => store.getState().toast(errorText(code, message), 'bad'));
  return s;
}

async function reauthenticate(s: GameSocket) {
  if (++authFailures > 3) return useAuth.getState().logout('Не удалось восстановить соединение, войдите снова');
  try {
    const auth = await refreshSession();
    if (auth) s.connect();
    else await useAuth.getState().logout('Сессия истекла, войдите снова');
  } catch (err) {
    if (err instanceof ApiError && err.code === 'SITE_CLOSED') return;
    await useAuth.getState().logout(banNotice(err) ?? undefined);
  }
}

export function disconnectSocket() {
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
  useGame.getState().reset();
}

/** Команда серверу с ack. Ошибки показываются тостом, если не попросили иначе. */
export function emit<T = unknown>(event: keyof ClientToServer, payload?: unknown, opts: { silent?: boolean } = {}): Promise<Ack<T>> {
  const s = socket;
  if (!s?.connected) {
    const res: Ack<T> = { ok: false, error: 'TIMEOUT', message: 'Нет соединения с сервером' };
    if (!opts.silent) useGame.getState().toast(res.message!, 'bad');
    return Promise.resolve(res);
  }
  return new Promise((resolve) => {
    const raw = s.timeout(8000) as unknown as { emit(e: string, ...a: unknown[]): void };
    const done = (err: Error | null, res?: Ack<T>) => {
      const result: Ack<T> = err || !res ? { ok: false, error: 'TIMEOUT' } : res;
      if (!result.ok && !opts.silent) useGame.getState().toast(errorText(result.error, result.message), 'bad');
      resolve(result);
    };
    if (payload === undefined) raw.emit(event, done);
    else raw.emit(event, payload, done);
  });
}
